<template>
  <div>
    <div class="rec-detail">
      <!-- Sidebar -->
      <div class="rec-sidebar">
        <div class="rec-sidebar-inner sticky">
          <div class="content">
            <template v-if="isHotel">
              <!-- Hotel category -->
              <h3 class="cat-sleep">Hotel</h3>

              <!-- Title -->
              <h2>{{ partner.title }}</h2>

              <!-- Star rating -->
              <div v-if="starRating" class="hotel-stars">
                <span v-for="n in starRating" :key="n" class="partner-star filled">&#9733;</span>
              </div>

              <!-- Brand -->
              <div v-if="metadata.brand_name" class="hotel-brand">{{ metadata.brand_name }}</div>

              <!-- Location -->
              <div v-if="locationText" class="partner-location-line">
                <svg width="10" height="13" viewBox="0 0 12 15" fill="none"><path d="M6 0C2.7 0 0 2.7 0 6c0 4.5 6 9 6 9s6-4.5 6-9c0-3.3-2.7-6-6-6zm0 8.2c-1.2 0-2.2-1-2.2-2.2S4.8 3.8 6 3.8 8.2 4.8 8.2 6 7.2 8.2 6 8.2z" fill="currentColor"/></svg>
                {{ locationText }}
              </div>

              <!-- Fora rating - temporarily disabled
              <div v-if="foraRating" class="hotel-fora-rating">
                <span class="partner-star filled">&#9733;</span>
                <span class="rating-value">{{ foraRating }}</span>
                <span class="rating-count">{{ metadata.total_review_count ? metadata.total_review_count.toLocaleString() : '' }} Fora reviews</span>
              </div>
              -->

              <!-- Room count -->
              <div v-if="metadata.total_rooms" class="hotel-rooms">{{ metadata.total_rooms }} rooms</div>

              <!-- Contact -->
              <div v-if="hotelContact.phones.length || hotelContact.emails.length" class="hotel-contact">
                <a v-if="hotelContact.phones.length" :href="'tel:' + hotelContact.phones[0]">{{ hotelContact.phones[0] }}</a>
                <a v-if="hotelContact.emails.length" :href="'mailto:' + hotelContact.emails[0]">{{ hotelContact.emails[0] }}</a>
              </div>

              <!-- Address + Google Maps -->
              <div v-if="metadata.physical_address" class="hotel-address">
                {{ metadata.physical_address }}
                <a v-if="metadata.gmaps_link" :href="metadata.gmaps_link" target="_blank" rel="noopener">View on Map</a>
              </div>
            </template>

            <template v-else>
              <!-- Category (like PLAY / EAT / SLEEP) -->
              <h3 :class="partner.type === 'experience' ? 'cat-play' : 'cat-eat'">
                {{ partner.type === 'experience' ? 'Experience' : 'Member Perk' }}
              </h3>

              <!-- Title (Clearface, large, like spot) -->
              <h2>{{ partner.title }}</h2>

              <!-- Meta line (like $$$ · Spa · American Southwest) -->
              <div class="partner-meta-line" v-if="metaItems.length">
                <span v-for="(item, idx) in metaItems" :key="idx">
                  {{ item }}<template v-if="idx < metaItems.length - 1"> · </template>
                </span>
              </div>

              <!-- Location -->
              <div v-if="locationText" class="partner-location-line">
                <svg width="10" height="13" viewBox="0 0 12 15" fill="none"><path d="M6 0C2.7 0 0 2.7 0 6c0 4.5 6 9 6 9s6-4.5 6-9c0-3.3-2.7-6-6-6zm0 8.2c-1.2 0-2.2-1-2.2-2.2S4.8 3.8 6 3.8 8.2 4.8 8.2 6 7.2 8.2 6 8.2z" fill="currentColor"/></svg>
                {{ locationText }}
              </div>

              <!-- Rating Stars (like loved icons) -->
              <div v-if="metadata.rating_average" class="partner-rating-line">
                <span v-for="n in 5" :key="n" class="partner-star" :class="{ filled: n <= Math.round(parseFloat(metadata.rating_average)) }">&#9733;</span>
                <span>{{ parseFloat(metadata.rating_average).toFixed(1) }}</span>
              </div>
            </template>
          </div>

          <!-- Bottom buttons (like REQUEST TO BOOK / LEAVE A REVIEW) -->
          <div class="buttons">
            <div v-if="isHotel" class="book-button-wrap">
              <span class="book-button" @click="openBookingModal" style="cursor:pointer">
                <svg class="book_icon"><use xlink:href="#book_icon"></use></svg>
                Book Hotel
                <svg class="arrow-right"><use xlink:href="#arrow-right"></use></svg>
              </span>
            </div>
            <div v-else-if="purchaseLink" class="book-button-wrap">
              <a :href="purchaseLink" target="_blank" rel="noopener" class="book-button">
                <svg class="book_icon"><use xlink:href="#book_icon"></use></svg>
                {{ partner.type === 'experience' ? 'Book Now' : btnLabel }}
                <svg class="arrow-right"><use xlink:href="#arrow-right"></use></svg>
              </a>
            </div>
            <div class="review-button-wrap">
              <span class="reviews-modal-trigger">
                <svg class="review-check"><use xlink:href="#review-check"></use></svg>
                Leave a Review
                <svg class="arrow-right"><use xlink:href="#arrow-right"></use></svg>
              </span>
            </div>
            <span class="list-add-trigger"
                  :data-id="partner.id"
                  :data-title="partner.title"
                  data-type="partner">
              <svg class="save"><use xlink:href="#save"></use></svg>
            </span>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="rec-main">
        <!-- Gallery (1 large + 2 small like spot) -->
        <div class="rec-gallery-wrap" v-if="gallery.length">
          <div class="partner-gallery-grid">
            <div class="partner-gallery-grid__main">
              <img :src="gallery[0]" :alt="partner.title" class="cursor-pointer" @click="openGallery(0)" />
            </div>
            <div class="partner-gallery-grid__side" v-if="gallery.length > 1">
              <div v-for="(img, idx) in gallery.slice(1, 3)" :key="idx">
                <img :src="img" :alt="partner.title" class="cursor-pointer" @click="openGallery(idx + 1)" />
              </div>
            </div>
            <div v-if="gallery.length > 3" class="partner-gallery-grid__count" @click="openGallery(0)">
              +{{ gallery.length - 3 }} photos
            </div>
          </div>
        </div>

        <div class="rec-content">
          <!-- Action buttons (same as spot: ADD TO LIST / COPY LINK) -->
          <div class="rec-extras">
            <div class="rec-extras-inner">
              <span class="rec-extra-button list-add-trigger spot-page"
                    :data-id="partner.id"
                    :data-title="partner.title"
                    data-type="partner">
                <svg class="save"><use xlink:href="#save"></use></svg>
                Add to list
              </span>
              <span class="rec-extra-button rec-copy-trigger" @click="copyLink">
                <svg class="copy"><use xlink:href="#copy"></use></svg>
                <span>{{ copied ? 'Copied!' : 'Copy link' }}</span>
              </span>
            </div>
          </div>

          <!-- Intro (italic like spot) -->
          <div class="rec-intro" v-if="partner.tagline">
            <p>{{ partner.tagline }}</p>
          </div>

          <!-- Hotel: Amenities -->
          <section v-if="isHotel && hotelAmenities.length" class="hotel-amenities">
            <h3 class="partner-section-title">Amenities</h3>
            <div class="amenity-grid">
              <div v-for="group in hotelAmenities" :key="group.name" class="amenity-card">
                <h4>{{ group.name }}</h4>
                <ul>
                  <li v-for="detail in group.amenity_details" :key="detail">{{ detail }}</li>
                </ul>
              </div>
            </div>
          </section>

          <!-- Non-hotel: Good For tags -->
          <div v-if="!isHotel && featureTags.length" class="partner-good-for">
            <span class="partner-good-for__label">Good for</span>
            <span v-for="tag in featureTags" :key="tag" class="partner-good-for__tag">{{ tag }}</span>
          </div>

          <!-- Non-hotel: Price display -->
          <div v-if="!isHotel && (partner.price_label || partner.price_value)" class="partner-price-inline">
            {{ partner.price_label || priceDisplay }}
          </div>

          <!-- Body -->
          <section class="recommendation-body" v-if="partner.summary || partner.description">
            <div v-if="partner.summary" v-html="partner.summary"></div>
            <div v-if="partner.description" v-html="partner.description" style="margin-top: 16px;"></div>
          </section>

          <!-- Non-hotel: What's Included -->
          <section class="recommendation-body" v-if="!isHotel && metadata.whats_included">
            <h3 class="partner-section-title">What's Included</h3>
            <div v-html="formatList(metadata.whats_included)"></div>
          </section>

          <!-- Non-hotel: What's Excluded -->
          <section class="recommendation-body" v-if="!isHotel && metadata.whats_excluded">
            <h3 class="partner-section-title">What's Excluded</h3>
            <div v-html="formatList(metadata.whats_excluded)"></div>
          </section>

          <!-- Non-hotel: Additional Info -->
          <section class="recommendation-body" v-if="!isHotel" v-for="(info, idx) in additionalInfo" :key="'info-' + idx">
            <h3 class="partner-section-title">{{ info.title }}</h3>
            <ul><li v-for="(desc, didx) in info.description" :key="didx">{{ desc }}</li></ul>
          </section>

          <!-- Reviews (like spot reviews section) -->
          <div class="reviews-section" v-if="partner.reviews && partner.reviews.length">
            <div class="reviews-header">
              <div class="reviews-header-main">
                <h2 id="reviews">Reviews</h2>
              </div>
            </div>
            <div v-for="review in partner.reviews" :key="review.id" class="partner-review-item">
              <div class="partner-review-item__avatar">
                <span>{{ review.user ? review.user.first_name.charAt(0) : '?' }}</span>
              </div>
              <div class="partner-review-item__body">
                <div class="partner-review-item__meta">
                  <strong v-if="review.user">{{ review.user.first_name }} {{ review.user.last_name }}</strong>
                  <span>{{ formatDate(review.created_at) }}</span>
                </div>
                <p>{{ review.body }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Gallery Modal -->
    <partner-gallery-modal :title="partner.title"></partner-gallery-modal>

    <!-- Hotel Booking Modal -->
    <hotel-booking-modal v-if="isHotel" :partner="partner"></hotel-booking-modal>

    <!-- Related -->
    <div v-if="related.length" class="partner-related-section wrapper">
      <h2 class="section-heading">You Might Also Like</h2>
      <div class="section-grid">
        <a v-for="r in related" :key="r.id" :href="'/partner/' + r.slug" class="section-card">
          <div class="section-card__image">
            <img v-if="getImage(r)" :src="getImage(r)" :alt="r.title" class="section-card__img" />
          </div>
          <div class="section-card__content">
            <h3 class="section-card__title">{{ r.title }}</h3>
            <div class="section-card__meta" v-if="r.price_label"><span class="section-card__price">{{ r.price_label }}</span></div>
          </div>
        </a>
      </div>
    </div>

    <div class="wrapper" style="text-align: center; padding-bottom: 40px;">
      <a :href="backUrl" class="btn-load-more">&larr; Back to {{ backLabel }}</a>
    </div>
  </div>
</template>

<script>
import PartnerGalleryModal from './GalleryModal.vue'
import HotelBookingModal from './HotelBookingModal.vue'

export default {
  components: { PartnerGalleryModal, HotelBookingModal },
  props: {
    partner: { type: Object, required: true },
    related: { type: Array, default: () => [] },
    section: { type: String, default: 'activities' }
  },
  data() {
    return { copied: false }
  },
  computed: {
    metadata() { return this.partner.metadata || {} },
    isHotel() { return this.partner.source === 'fora' },
    starRating() { return this.metadata.hotel_class || null },
    foraRating() {
      const r = this.metadata.average_review_rating
      return r ? parseFloat(r).toFixed(1) : null
    },
    hotelLabels() { return this.metadata.labels || [] },
    hotelPerks() { return this.metadata.perks || [] },
    hotelAmenities() { return this.metadata.amenities || [] },
    hotelContact() {
      const c = this.metadata.contact_info || {}
      return { phones: c.phones || [], emails: c.emails || [] }
    },
    gallery() {
      if (!Array.isArray(this.partner.images)) return []
      return this.partner.images.map(img => {
        if (typeof img === 'string') return img
        return img.large || img.medium || img.small
      }).filter(Boolean)
    },
    locationText() {
      if (this.partner.city) return this.partner.city.name || this.partner.city.title
      if (this.partner.country) return this.partner.country.name
      return null
    },
    priceDisplay() {
      if (this.partner.price_value) return `From ${this.partner.currency || 'USD'} ${parseFloat(this.partner.price_value).toFixed(0)}`
      return ''
    },
    purchaseLink() { return this.partner.purchase_link || this.metadata.checkout_url || this.partner.url },
    btnLabel() { return this.metadata.btn_label || 'View Offer' },
    additionalInfo() { return this.metadata.additional_info || [] },
    metaItems() {
      const items = []
      if (this.partner.price_label) items.push(this.partner.price_label)
      if (this.partner.category) items.push(this.partner.category.charAt(0).toUpperCase() + this.partner.category.slice(1))
      if (this.locationText) items.push(this.locationText)
      return items
    },
    featureTags() {
      const tags = []
      if (this.metadata.skip_line) tags.push('Skip the Line')
      if (this.metadata.instant_ticket_delivery) tags.push('Instant Delivery')
      if (this.metadata.smartphone_ticket) tags.push('Mobile Ticket')
      if (this.metadata.wheelchair_access) tags.push('Wheelchair Accessible')
      if (this.partner.duration) tags.push(this.partner.duration)
      return tags
    },
    backUrl() {
      const map = { activities: '/activities', hotels: '/hotels' }
      return map[this.section] || '/travel'
    },
    backLabel() {
      const map = { activities: 'Activities', hotels: 'Hotels' }
      return map[this.section] || 'Travel'
    },
    venueDetails() {
      const d = []
      if (this.metadata.venue_name) d.push({ label: 'Venue', value: this.metadata.venue_name })
      if (this.metadata.languages?.length) d.push({ label: 'Languages', value: this.metadata.languages.map(l => l.toUpperCase()).slice(0, 8).join(', ') })
      if (this.metadata.timezone) d.push({ label: 'Timezone', value: this.metadata.timezone })
      if (this.metadata.rating_total) d.push({ label: 'Reviews', value: this.metadata.rating_total.toLocaleString() + ' reviews' })
      return d
    }
  },
  methods: {
    formatList(text) {
      if (!text) return ''
      return '<ul>' + text.split('\n').filter(l => l.trim()).map(l => `<li>${l.replace(/^\* /, '').trim()}</li>`).join('') + '</ul>'
    },
    formatDate(date) {
      return date ? new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : ''
    },
    getImage(p) {
      if (!Array.isArray(p.images) || !p.images.length) return ''
      const img = p.images[0]
      if (typeof img === 'string') return img
      return img.medium || img.large || ''
    },
    formatPerks(text) {
      if (!text) return ''
      return '<ul>' + text.split('\n').filter(l => l.trim()).map(l => `<li>${l.replace(/^\*\s*/, '').trim()}</li>`).join('') + '</ul>'
    },
    openGallery(index) {
      eventBus.$emit('open-partner-gallery', {
        images: this.gallery,
        index: index
      })
    },
    copyLink() {
      navigator.clipboard.writeText(window.location.href)
      this.copied = true
      setTimeout(() => { this.copied = false }, 2000)
    },
    openBookingModal() {
      eventBus.$emit('open-hotel-booking')
    }
  }
}
</script>
